import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";

import {
  askedGuestEndsOnByDefault,
  InvolveGuestAsAssessorRequestSchema,
  professionalRelations,
  type EarlierGuest,
  type EmailAddress,
  type InvolveGuestAsAssessorRequest,
  type ProfessionalRelation,
  type UserId,
} from "@thalamos/haven-common";

import { CancelOrProceedActions } from "../CancelOrProceedActions/CancelOrProceedActions.js";

const UTC_CALENDAR_DATE = new Intl.DateTimeFormat("en-GB", {
  dateStyle: "long",
  // The day is a calendar day, so it is read and shown at midnight UTC.
  timeZone: "UTC",
});

/** A UK calendar day, `YYYY-MM-DD`, as the dialog words it. */
export const calendarDayText = (day: string): string =>
  UTC_CALENDAR_DATE.format(new Date(day));

/** Looking up who an address reached before: the earlier guests, or a failure. */
export type EarlierGuestsMatch =
  | { kind: "matched"; earlierGuests: readonly EarlierGuest[] }
  | { kind: "failed" };

/**
 * How asking went: asked, refused for the address in the server's words,
 * refused because the guest is already asked about this person, or
 * not reached.
 */
export type AskOutcome =
  | { kind: "asked" }
  | { kind: "address_refused"; message: string }
  | { kind: "already_asked" }
  | { kind: "no_date_of_birth" }
  | { kind: "failed" };

type Field =
  "name" | "email" | "relationDescription" | "jobTitle" | "message" | "endsOn";

/** Where asking stands: filling in, or saying whether an earlier guest is the one asked. */
type Step =
  | { kind: "details" }
  | {
      kind: "earlier_guest";
      request: InvolveGuestAsAssessorRequest;
      earlierGuests: readonly EarlierGuest[];
    };

/** The value of the radio that says the guest asked is none of the earlier guests. */
const SOMEBODY_NEW = "somebody_new";

/**
 * Asks somebody outside the service to complete the whole of an assessment
 * about one person: who they are, an optional message, and the day
 * their access ends, a month from today unless another is chosen.
 *
 * Where the address reached a guest before, on a person the asker reaches,
 * the asker says whether this is the guest they mean before the request is
 * sent.
 */
export const AskGuestDialog = ({
  open,
  personName,
  versions,
  versionsFailed,
  onMatch,
  onAsk,
  onClose,
}: {
  open: boolean;
  /** Who the assessment is about, as the dialog names them. */
  personName: string;
  /** The versions the acting body may use; undefined until they have been read. */
  versions: readonly string[] | undefined;
  versionsFailed: boolean;
  onMatch: (email: EmailAddress) => Promisable<EarlierGuestsMatch>;
  onAsk: (request: InvolveGuestAsAssessorRequest) => Promisable<AskOutcome>;
  onClose: () => Promisable<void>;
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [relation, setRelation] = useState<ProfessionalRelation | "">("");
  const [relationDescription, setRelationDescription] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [message, setMessage] = useState("");
  const [defaultEnd] = useState(() => askedGuestEndsOnByDefault());
  const [endChoice, setEndChoice] = useState<"default" | "chosen">("default");
  const [chosenDay, setChosenDay] = useState("");
  const [chosenVersion, setChosenVersion] = useState("");
  const [triedToSend, setTriedToSend] = useState(false);
  const [step, setStep] = useState<Step>({ kind: "details" });
  const [earlierGuestUserId, setEarlierGuestUserId] = useState<
    UserId | typeof SOMEBODY_NEW | ""
  >("");
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<AskOutcome>();

  const onlyVersion = versions?.length === 1 ? versions[0] : undefined;
  const instrument = onlyVersion ?? chosenVersion;
  const parsed = InvolveGuestAsAssessorRequestSchema.safeParse({
    name,
    email: email.trim(),
    earlierGuestUserId: null,
    relation: {
      relationGroup: "professional",
      relation,
      relationDescription: relation === "other" ? relationDescription : null,
    },
    jobTitle,
    message,
    endsOn: endChoice === "default" ? defaultEnd : chosenDay,
    instrument,
  });

  // The schema's messages are translation keys; an address it refuses says
  // so in words of the dialog's own.
  const problemWith = (field: Field): string | undefined => {
    const issue = parsed.error?.issues.find((each) =>
      field === "relationDescription"
        ? each.path[0] === "relation" && each.path[1] === field
        : each.path[0] === field,
    );

    if (!triedToSend || issue === undefined) {
      return undefined;
    }

    return field === "email"
      ? t("askGuest.emailInvalid")
      : t(issue.message, { defaultValue: t("askGuest.invalid") });
  };
  const relationMissing = triedToSend && relation === "";
  const versionMissing = triedToSend && instrument === "";
  const addressRefusal =
    outcome?.kind === "address_refused" ? outcome.message : undefined;

  const ask = async (request: InvolveGuestAsAssessorRequest) => {
    setOutcome(undefined);
    setSending(true);

    try {
      const asked = await onAsk(request);
      setOutcome(asked);
      if (asked.kind === "address_refused") {
        setStep({ kind: "details" });
      }
    } catch {
      setOutcome({ kind: "failed" });
    } finally {
      setSending(false);
    }
  };

  const sendDetails = async () => {
    setTriedToSend(true);

    if (!parsed.success) {
      return;
    }

    setOutcome(undefined);
    setSending(true);

    let matched: EarlierGuestsMatch;
    try {
      matched = await onMatch(parsed.data.email);
    } catch {
      matched = { kind: "failed" };
    }
    setSending(false);

    if (matched.kind === "failed") {
      setOutcome({ kind: "failed" });
      return;
    }

    if (matched.earlierGuests.length > 0) {
      setEarlierGuestUserId("");
      setStep({
        kind: "earlier_guest",
        request: parsed.data,
        earlierGuests: matched.earlierGuests,
      });
      return;
    }

    await ask(parsed.data);
  };

  const sendWithEarlierGuest = async (
    request: InvolveGuestAsAssessorRequest,
  ) => {
    if (earlierGuestUserId === "") {
      return;
    }

    await ask({
      ...request,
      earlierGuestUserId:
        earlierGuestUserId === SOMEBODY_NEW ? null : earlierGuestUserId,
    });
  };

  const failure =
    outcome?.kind === "already_asked" ||
    outcome?.kind === "no_date_of_birth" ||
    outcome?.kind === "failed" ? (
      <Alert severity="error">
        {t(`askGuest.outcome.${outcome.kind}`, { personName })}
      </Alert>
    ) : null;

  return (
    <Dialog
      open={open}
      // Nothing closes the dialog while a request is on its way, so its
      // outcome is never lost.
      onClose={
        sending
          ? undefined
          : () => {
              void onClose();
            }
      }
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t("askGuest.title")}</DialogTitle>
      {step.kind === "earlier_guest" ? (
        <>
          <DialogContent>
            <Stack spacing={2}>
              <FormControl>
                <FormLabel id="ask-guest-earlier">
                  {t("askGuest.earlierGuest.question", {
                    email: step.request.email,
                  })}
                </FormLabel>
                <RadioGroup
                  aria-labelledby="ask-guest-earlier"
                  value={earlierGuestUserId}
                  onChange={(event) => {
                    const chosen = step.earlierGuests.find(
                      (guest) => guest.userId === event.target.value,
                    );
                    setEarlierGuestUserId(chosen?.userId ?? SOMEBODY_NEW);
                  }}
                >
                  {step.earlierGuests.map((guest) => (
                    <FormControlLabel
                      key={guest.userId}
                      value={guest.userId}
                      control={<Radio />}
                      disabled={sending}
                      label={
                        guest.jobTitle === null
                          ? guest.name
                          : t("askGuest.earlierGuest.withJobTitle", {
                              name: guest.name,
                              jobTitle: guest.jobTitle,
                            })
                      }
                    />
                  ))}
                  <FormControlLabel
                    value={SOMEBODY_NEW}
                    control={<Radio />}
                    disabled={sending}
                    label={t("askGuest.earlierGuest.somebodyNew", {
                      name: step.request.name,
                    })}
                  />
                </RadioGroup>
              </FormControl>
              {failure}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              disabled={sending}
              onClick={() => {
                setOutcome(undefined);
                setStep({ kind: "details" });
              }}
            >
              {t("askGuest.back")}
            </Button>
            <Button
              variant="contained"
              disabled={sending || earlierGuestUserId === ""}
              onClick={() => {
                void sendWithEarlierGuest(step.request);
              }}
            >
              {t("askGuest.send")}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <DialogContentText>
                {t("askGuest.intro", { personName })}
              </DialogContentText>
              <TextField
                label={t("askGuest.name")}
                value={name}
                disabled={sending}
                error={problemWith("name") !== undefined}
                helperText={problemWith("name")}
                onChange={(event) => {
                  setName(event.target.value);
                }}
              />
              <TextField
                label={t("askGuest.email")}
                type="email"
                value={email}
                disabled={sending}
                error={
                  problemWith("email") !== undefined ||
                  addressRefusal !== undefined
                }
                helperText={problemWith("email") ?? addressRefusal}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setOutcome(undefined);
                }}
              />
              <TextField
                select
                label={t("askGuest.relation")}
                value={relation}
                disabled={sending}
                error={relationMissing}
                helperText={
                  relationMissing ? t("askGuest.relationRequired") : undefined
                }
                onChange={(event) => {
                  const chosen = professionalRelations.find(
                    (each) => each === event.target.value,
                  );
                  setRelation(chosen ?? "");
                }}
              >
                {/* Guests are professionals reached at a work address, so
                    the family and carer groups are not offered. */}
                {professionalRelations.map((each) => (
                  <MenuItem key={each} value={each}>
                    {t(`standing.relation.professional.${each}`)}
                  </MenuItem>
                ))}
              </TextField>
              {relation === "other" ? (
                <TextField
                  label={t("askGuest.relationDescription")}
                  value={relationDescription}
                  disabled={sending}
                  error={problemWith("relationDescription") !== undefined}
                  helperText={problemWith("relationDescription")}
                  onChange={(event) => {
                    setRelationDescription(event.target.value);
                  }}
                />
              ) : null}
              <TextField
                label={t("askGuest.jobTitle")}
                value={jobTitle}
                disabled={sending}
                error={problemWith("jobTitle") !== undefined}
                helperText={problemWith("jobTitle")}
                onChange={(event) => {
                  setJobTitle(event.target.value);
                }}
              />
              <TextField
                label={t("askGuest.message")}
                multiline
                minRows={3}
                value={message}
                disabled={sending}
                error={problemWith("message") !== undefined}
                helperText={problemWith("message") ?? t("askGuest.messageHelp")}
                onChange={(event) => {
                  setMessage(event.target.value);
                }}
              />
              {versions !== undefined && versions.length > 1 ? (
                <TextField
                  select
                  label={t("askGuest.version")}
                  value={chosenVersion}
                  disabled={sending}
                  error={versionMissing}
                  helperText={
                    versionMissing ? t("askGuest.versionRequired") : undefined
                  }
                  onChange={(event) => {
                    setChosenVersion(event.target.value);
                  }}
                >
                  {versions.map((version) => (
                    <MenuItem key={version} value={version}>
                      {version}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
              <FormControl>
                <FormLabel id="ask-guest-ends">
                  {t("askGuest.endsOn")}
                </FormLabel>
                <RadioGroup
                  aria-labelledby="ask-guest-ends"
                  value={endChoice}
                  onChange={(event) => {
                    setEndChoice(
                      event.target.value === "chosen" ? "chosen" : "default",
                    );
                  }}
                >
                  <FormControlLabel
                    value="default"
                    control={<Radio />}
                    disabled={sending}
                    label={t("askGuest.endsAfterAMonth", {
                      date: calendarDayText(defaultEnd),
                    })}
                  />
                  <FormControlLabel
                    value="chosen"
                    control={<Radio />}
                    disabled={sending}
                    label={t("askGuest.endsOnChosenDay")}
                  />
                </RadioGroup>
              </FormControl>
              {endChoice === "chosen" ? (
                <TextField
                  type="date"
                  label={t("askGuest.chosenDay")}
                  value={chosenDay}
                  disabled={sending}
                  error={problemWith("endsOn") !== undefined}
                  helperText={
                    problemWith("endsOn") ?? t("askGuest.chosenDayHelp")
                  }
                  onChange={(event) => {
                    setChosenDay(event.target.value);
                  }}
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{ maxWidth: 240 }}
                />
              ) : null}
              {versionsFailed ? (
                <Alert severity="error">{t("askGuest.versionsFailed")}</Alert>
              ) : versions?.length === 0 ? (
                <Alert severity="info">{t("askGuest.noVersions")}</Alert>
              ) : null}
              {failure}
            </Stack>
          </DialogContent>
          <CancelOrProceedActions
            cancel={{
              onClick: onClose,
              disabled: sending,
            }}
            proceed={{
              label: t("askGuest.send"),
              onClick: sendDetails,
              disabled:
                sending || versions === undefined || versions.length === 0,
              kind: "primary",
            }}
          />
        </>
      )}
    </Dialog>
  );
};
